/**
 * Formatting utilities for axis labels, tooltips, and text displays.
 * Handles time, price, and volume formatting with localization support.
 */

import { DAY, HOUR, WEEK, YEAR } from "./constants";
import type { DataInterval } from "~types/types";

// ==================== Time Formatting ====================

/**
 * Time format precision levels.
 */
export type TimeFormatPrecision =
  | "year"
  | "month"
  | "day"
  | "hour"
  | "minute"
  | "second";

/**
 * Determine appropriate precision based on time range.
 * Thresholds optimized for readable axis labels:
 * - < 1 hour: seconds (e.g., "10:45:30")
 * - < 1 day: minutes (e.g., "10:45")
 * - < 1 week: hours (e.g., "10:00") - just time for intraday
 * - < 1 year: days (e.g., "Jan 15") - month + date
 * - < 3 years: months (e.g., "Jan")
 * - >= 3 years: years (e.g., "2024")
 */
export function getTimePrecision(rangeMs: number): TimeFormatPrecision {
  if (rangeMs < HOUR) return "second";
  if (rangeMs < DAY) return "minute";
  if (rangeMs < WEEK) return "hour";
  if (rangeMs < YEAR / 2) return "day";
  if (rangeMs < 3 * YEAR) return "month";

  return "year";
}

/**
 * Derive time format precision from a data interval.
 * Maps interval granularity to the appropriate display precision:
 * - 1m, 5m, 15m, 30m → minute (show hours:minutes)
 * - 1h, 4h → hour (show hours:minutes)
 * - 1d → day (show month + day)
 * - 1w, 1M → month (show month name)
 * - 1y → year (show year)
 */
export function intervalToPrecision(
  interval: DataInterval
): TimeFormatPrecision {
  switch (interval) {
    case "1m":
    case "5m":
    case "15m":
    case "30m":
      return "minute";
    case "1h":
    case "2h":
    case "4h":
      return "hour";
    case "1d":
      return "day";
    case "1w":
    case "1M":
      return "month";
    case "1y":
      return "year";
    default: {
      // Exhaustive check - TypeScript ensures all cases are handled
      return interval;
    }
  }
}

/**
 * Format timestamp for axis display.
 * @param timestamp - Timestamp in milliseconds
 * @param precision - Time precision level
 * @param locale - Locale for formatting (default: 'en-US')
 * @param includeYear - For 'month' precision, whether to include year (default: false)
 */
export function formatTimestamp(
  timestamp: number,
  precision?: TimeFormatPrecision,
  locale = "en-US",
  includeYear = false
): string {
  const date = new Date(timestamp);
  const options: Intl.DateTimeFormatOptions = {};

  const actualPrecision = precision ?? "day";

  switch (actualPrecision) {
    case "second":
      options.hour = "2-digit";
      options.minute = "2-digit";
      options.second = "2-digit";
      options.hour12 = false;
      break;
    case "minute":
      options.hour = "2-digit";
      options.minute = "2-digit";
      options.hour12 = false;
      break;
    case "hour":
      // Just time for intraday/within-week ranges
      options.hour = "2-digit";
      options.minute = "2-digit";
      options.hour12 = false;
      if (includeYear) {
        options.month = "short";
        options.day = "numeric";
      }
      break;
    case "day":
      options.month = "short";
      options.day = "numeric";
      if (includeYear) {
        options.year = "numeric";
      }
      break;
    case "month":
      // Show just month name by default (year shown as secondary label)
      options.month = "short";
      if (includeYear) {
        options.year = "numeric";
      }
      break;
    case "year":
      options.year = "numeric";
      break;
  }

  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    // Fallback for unsupported locales
    return date.toISOString().substring(0, 10);
  }
}

/**
 * Format timestamp for tooltip (more detailed).
 */
export function formatTimestampFull(
  timestamp: number,
  locale = "en-US"
): string {
  const date = new Date(timestamp);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    return date.toISOString().replace("T", " ").substring(0, 16);
  }
}

// ==================== Price Formatting ====================

/**
 * Options for price formatting.
 */
export interface PriceFormatOptions {
  /** ISO currency code (USD, EUR, etc.) */
  currency?: string;
  /** Decimal places (auto if omitted) */
  decimals?: number;
  /** Use compact notation (1.5M instead of 1,500,000) */
  compact?: boolean;
  /** Show +/- for positive/negative */
  showSign?: boolean;
  /** Locale for number formatting */
  locale?: string;
}

/**
 * Determine appropriate decimal places based on value magnitude.
 */
export function getDecimalPlaces(value: number): number {
  const absValue = Math.abs(value);

  if (absValue >= 1) return 2;
  if (absValue >= 0.01) return 4;

  return 6;
}

/**
 * Format price value for display.
 */
export function formatPrice(
  value: number,
  options: PriceFormatOptions = {}
): string {
  const {
    currency,
    decimals = getDecimalPlaces(value),
    compact = false,
    showSign = false,
    locale = "en-US",
  } = options;

  const formatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    notation: compact ? "compact" : "standard",
    signDisplay: showSign ? "exceptZero" : "auto",
  };

  if (currency) {
    formatOptions.style = "currency";
    formatOptions.currency = currency;
  }

  try {
    return new Intl.NumberFormat(locale, formatOptions).format(value);
  } catch {
    // Fallback
    const sign = showSign && value > 0 ? "+" : "";

    return `${sign}${value.toFixed(decimals)}`;
  }
}

// ==================== Volume Formatting ====================

/**
 * Format volume for display (always compact).
 */
export function formatVolume(volume: number, locale = "en-US"): string {
  const absVolume = Math.abs(volume);

  if (absVolume < 1000) {
    return volume.toFixed(0);
  }

  const formatOptions: Intl.NumberFormatOptions = {
    notation: "compact",
    maximumFractionDigits: 1,
  };

  try {
    return new Intl.NumberFormat(locale, formatOptions).format(volume);
  } catch {
    // Manual fallback
    if (absVolume >= 1e9) return `${(volume / 1e9).toFixed(1)}B`;
    if (absVolume >= 1e6) return `${(volume / 1e6).toFixed(1)}M`;

    return `${(volume / 1e3).toFixed(1)}K`;
  }
}

// ==================== Percentage Formatting ====================

/**
 * Calculate percentage change between two values.
 */
export function calculatePercentageChange(
  oldValue: number,
  newValue: number
): number {
  if (oldValue === 0) return 0;

  return ((newValue - oldValue) / oldValue) * 100;
}

/**
 * Format percentage change.
 */
export function formatPercentage(
  value: number,
  decimals = 2,
  showSign = true
): string {
  const sign = showSign && value > 0 ? "+" : "";

  return `${sign}${value.toFixed(decimals)}%`;
}

// ==================== Currency Symbols ====================

/**
 * Common currency symbols (fallback when Intl not available).
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  INR: "₹",
  CNY: "¥",
  KRW: "₩",
  AUD: "A$",
  CAD: "C$",
  CHF: "CHF",
  BRL: "R$",
  RUB: "₽",
  ZAR: "R",
};

/**
 * Get currency symbol from ISO code.
 */
export function getCurrencySymbol(
  currencyCode: string,
  locale = "en-US"
): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);

    const symbolPart = parts.find((part) => part.type === "currency");

    return symbolPart?.value ?? CURRENCY_SYMBOLS[currencyCode] ?? currencyCode;
  } catch {
    return CURRENCY_SYMBOLS[currencyCode] ?? currencyCode;
  }
}

// ==================== Axis Formatters ====================

/**
 * Create x-axis formatter based on time range.
 */
export function createXAxisFormatter(
  rangeMs: number,
  locale = "en-US"
): (timestamp: number) => string {
  const precision = getTimePrecision(rangeMs);

  return (timestamp: number) => formatTimestamp(timestamp, precision, locale);
}

/**
 * Create y-axis formatter based on value range and currency.
 */
export function createYAxisFormatter(
  domain: [number, number],
  currency?: string,
  locale = "en-US"
): (value: number) => string {
  const [min, max] = domain;
  const range = max - min;

  // Determine if we should use compact notation
  const useCompact = Math.abs(max) >= 1000000 || Math.abs(min) >= 1000000;

  // Determine decimal places based on range
  let decimals = 0;

  if (range < 10) {
    decimals = 2;
  } else if (range < 100) {
    decimals = 1;
  }

  return (value: number) =>
    formatPrice(value, {
      currency,
      decimals,
      compact: useCompact,
      locale,
    });
}

// ==================== Tooltip Formatters ====================

/**
 * Extract currency from first series in meta map.
 * Defaults to USD when unavailable.
 */
export function getFirstSeriesCurrency(
  seriesMeta: Map<string, { issuedCurrency?: string }>
): string {
  const first = seriesMeta.values().next().value;

  return first?.issuedCurrency ?? "USD";
}
